import { Collapse } from '@mui/material'
import {
  AccountPerformanceOnDay,
  ApiNoteInProgress,
  ApiNoteInProgressAssociatedTrade,
  ApiPortfolioType,
  useGetByTenantAccountQuery
} from 'api/rtk-api'
import ChartIcon from 'assets/chart-line.svg?react'
import clsx from 'clsx'
import {
  Card,
  LabelValueRow,
  RotatingChevronIcon,
  Rule,
  TradeStatusAlert
} from 'components/core-v2'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { formatCurrencyFull } from 'utilities/currency'

import { CURRENCY, PATH, TENANT } from '../../../const'
import { Last30DaysChart } from '../../reporting/components/trades-table/Last30DaysChart'
import classes from './top-up-withdraw-tab.module.scss'

interface ExtendedTrade extends ApiNoteInProgressAssociatedTrade {
  accountSource: string
  currentValue: number
  last30DaysPeformancePercent: number
  last30DaysPerformance: AccountPerformanceOnDay[]
  newValue: number
  portfolioType: ApiPortfolioType
  strategyTemplateName: string
}
interface TradeProps {
  trade: ExtendedTrade
}
const Trade = ({ trade }: TradeProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const isPositive = trade.last30DaysPeformancePercent >= 0
  const chartData = trade.last30DaysPerformance.map(
    day => [new Date(day.date).getTime(), day.value] as [number, number]
  )
  return (
    <Card.Wrapper
      className={clsx(classes.trade, {
        [classes.shadow]: isOpen
      })}
      spacing="xs"
    >
      <button type="button" onClick={() => setIsOpen(!isOpen)}>
        <span className={classes.accountName}>
          <RotatingChevronIcon isRotated={isOpen} />
          {trade.accountName}
        </span>
        <span className={classes.amount}>
          {formatCurrencyFull(trade.instructionAmount, CURRENCY)}
        </span>
        <span className={classes.currentValue}>
          {formatCurrencyFull(trade.currentValue, CURRENCY)}
        </span>
        <span className={classes.newValue}>{formatCurrencyFull(trade.newValue, CURRENCY)}</span>
      </button>
      <Collapse in={isOpen}>
        <Rule />
        <TradeStatusAlert
          className="mt-4"
          tradeId={trade.accountId}
          tradeSource={trade.accountSource}
        />
        <div className={classes.infoWrapper}>
          <div className={classes.info}>
            <h5>Trade ID</h5>
            <span>{trade.accountId}</span>
          </div>
          <div className={classes.info}>
            <h5>Portfolio</h5>
            <span>{trade.portfolioType}</span>
          </div>
          <div className={classes.info}>
            <h5>Strategy</h5>
            <span>{trade.strategyTemplateName}</span>
          </div>
          <div className={clsx(classes.info, 'ml-auto')}>
            <h5>Last 7 days</h5>
            <span
              className={clsx({
                positive: isPositive,
                negative: !isPositive
              })}
            >
              {isPositive && '+'}
              {`${trade.last30DaysPeformancePercent.toFixed(2)}%`}
            </span>
          </div>
          <Last30DaysChart data={chartData} isPositive={isPositive} />
        </div>

        <Rule />
        <Link to={PATH.reportsSingleTradePerformance(trade.accountSource, trade.accountId)}>
          <ChartIcon />
          View trade report
        </Link>
      </Collapse>
    </Card.Wrapper>
  )
}

interface TopUpWithdrawTabProps {
  inProgressNote: ApiNoteInProgress
}
export const TopUpWithdrawTab = ({ inProgressNote }: TopUpWithdrawTabProps) => {
  const { data: accountData, isLoading: isAccountLoading } = useGetByTenantAccountQuery({
    tenant: TENANT,
    noteIds: inProgressNote.noteId
  })

  if (isAccountLoading) return null

  const isWithdrawal = inProgressNote.ongoingInstructionType === 'Withdrawal'

  const extendedTrades = inProgressNote.associatedTrades.map(trade => {
    const matchingAccount = accountData.accounts.find(
      account => account.accountId === trade.accountId
    )
    return {
      ...trade,
      accountSource: matchingAccount.accountSource,
      portfolioType: matchingAccount.portfolioType,
      strategyTemplateName: matchingAccount.strategyTemplateName,
      last30DaysPeformancePercent: matchingAccount.last30DaysPeformancePercent,
      last30DaysPerformance: matchingAccount.last30DaysPerformance,
      currentValue: Number(matchingAccount.accountValue),
      newValue:
        Number(matchingAccount.accountValue) +
        (isWithdrawal ? -1 : 1) * Number(trade.instructionAmount)
    }
  })

  return (
    <>
      <LabelValueRow
        showBottomBorder
        className="mb-8"
        label="Operation type"
        value={inProgressNote.ongoingInstructionType === 'Deposit' ? 'Top up' : 'Withdraw'}
      />
      <div className={classes.headingRow}>
        <span>Name</span>
        <span>{inProgressNote.ongoingInstructionType === 'Deposit' ? 'Top up' : 'Withdrawal'}</span>
        <span>Current value</span>
        <span>New value</span>
      </div>
      <ul>
        {extendedTrades.map(trade => (
          <Trade key={trade.accountId} trade={trade} />
        ))}
      </ul>
    </>
  )
}
